import DateTime from "../core/DateTime";
import PaymentStatus from "../classes/PaymentStatus";
import DBObject from "../utils/DBObject";
import CloneContext from "../utils/CloneContext";
import Hospital from "./Hospital";

export default class Invoice extends DBObject {
  private static readonly _AMOUNT: number = 0;
  private static readonly _DUEDATE: number = 1;
  private static readonly _HOSPITAL: number = 2;
  private static readonly _INVOICEDATE: number = 3;
  private static readonly _INVOICEID: number = 4;
  private static readonly _INVOICEMONTH: number = 5;
  private static readonly _REQUESTCOUNT: number = 6;
  private static readonly _STATUS: number = 7;
  private static readonly _TITLE: number = 8;
  public id: number = 0;
  public otherMaster: DBObject;
  private _title: string = "";
  private _invoiceDate: DateTime = null;
  private _dueDate: DateTime = null;
  private _amount: number = 0.0;
  private _requestCount: number = 0;
  private _status: PaymentStatus = PaymentStatus.Unpaid;
  private _invoiceId: string = "";
  private _invoiceMonth: number = 0;
  private _hospital: Hospital = null;
  public constructor(
    d3eParams?: Partial<{
      amount: number;
      dueDate: DateTime;
      hospital: Hospital;
      invoiceDate: DateTime;
      invoiceId: string;
      invoiceMonth: number;
      requestCount: number;
      status: PaymentStatus;
      title: string;
    }>
  ) {
    super();

    this.setAmount(d3eParams?.amount ?? 0.0);

    this.setDueDate(d3eParams?.dueDate ?? null);

    this.setHospital(d3eParams?.hospital ?? null);

    this.setInvoiceDate(d3eParams?.invoiceDate ?? null);

    this.setInvoiceId(d3eParams?.invoiceId ?? "");

    this.setInvoiceMonth(d3eParams?.invoiceMonth ?? 0);

    this.setRequestCount(d3eParams?.requestCount ?? 0);

    this.setStatus(d3eParams?.status ?? PaymentStatus.Unpaid);

    this.setTitle(d3eParams?.title ?? "");
  }
  public get d3eType(): string {
    return "Invoice";
  }
  public clear(): void {
    this.d3eChanges.clear();
  }
  public get title(): string {
    return this._title;
  }
  public setTitle(val: string): void {
    let isValChanged: boolean = this._title !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(Invoice._TITLE, this._title);

    this._title = val;

    this.fire("title", this);
  }
  public get invoiceDate(): DateTime {
    return this._invoiceDate;
  }
  public setInvoiceDate(val: DateTime): void {
    let isValChanged: boolean = this._invoiceDate !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(Invoice._INVOICEDATE, this._invoiceDate);

    this._invoiceDate = val;

    this.fire("invoiceDate", this);
  }
  public get dueDate(): DateTime {
    return this._dueDate;
  }
  public setDueDate(val: DateTime): void {
    let isValChanged: boolean = this._dueDate !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(Invoice._DUEDATE, this._dueDate);

    this._dueDate = val;

    this.fire("dueDate", this);
  }
  public get amount(): number {
    return this._amount;
  }
  public setAmount(val: number): void {
    let isValChanged: boolean = this._amount !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(Invoice._AMOUNT, this._amount);

    this._amount = val;

    this.fire("amount", this);
  }
  public get requestCount(): number {
    return this._requestCount;
  }
  public setRequestCount(val: number): void {
    let isValChanged: boolean = this._requestCount !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(Invoice._REQUESTCOUNT, this._requestCount);

    this._requestCount = val;

    this.fire("requestCount", this);
  }
  public get status(): PaymentStatus {
    return this._status;
  }
  public setStatus(val: PaymentStatus): void {
    let isValChanged: boolean = this._status !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(Invoice._STATUS, this._status.index);

    this._status = val;

    this.fire("status", this);
  }
  public get invoiceId(): string {
    return this._invoiceId;
  }
  public setInvoiceId(val: string): void {
    let isValChanged: boolean = this._invoiceId !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(Invoice._INVOICEID, this._invoiceId);

    this._invoiceId = val;

    this.fire("invoiceId", this);
  }
  public get invoiceMonth(): number {
    return this._invoiceMonth;
  }
  public setInvoiceMonth(val: number): void {
    let isValChanged: boolean = this._invoiceMonth !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(Invoice._INVOICEMONTH, this._invoiceMonth);

    this._invoiceMonth = val;

    this.fire("invoiceMonth", this);
  }
  public get hospital(): Hospital {
    return this._hospital;
  }
  public setHospital(val: Hospital): void {
    let isValChanged: boolean = this._hospital !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(Invoice._HOSPITAL, this._hospital);

    this.updateObservable("hospital", this._hospital, val);

    this._hospital = val;

    this.fire("hospital", this);
  }
  public get(field: number): any {
    switch (field) {
      case Invoice._TITLE: {
        return this._title;
      }

      case Invoice._INVOICEDATE: {
        return this._invoiceDate;
      }

      case Invoice._DUEDATE: {
        return this._dueDate;
      }

      case Invoice._AMOUNT: {
        return this._amount;
      }

      case Invoice._REQUESTCOUNT: {
        return this._requestCount;
      }

      case Invoice._STATUS: {
        return this._status.index;
      }

      case Invoice._INVOICEID: {
        return this._invoiceId;
      }

      case Invoice._INVOICEMONTH: {
        return this._invoiceMonth;
      }

      case Invoice._HOSPITAL: {
        return this._hospital;
      }
      default: {
        return null;
      }
    }
  }
  public updateD3EChanges(index: number, value: any): void {
    if (this.lockedChanges()) {
      return;
    }

    super.updateD3EChanges(index, value);
  }
  public restore(): void {
    /*
TODO: Might be removed
*/

    this.d3eChanges.restore(this);
  }
  public deepClone(clearId = true): Invoice {
    let ctx: CloneContext = new CloneContext({ "clearId": clearId });

    return ctx.startClone(this);
  }
  public collectChildValues(ctx: CloneContext): void {}
  public deepCloneIntoObj(dbObj: DBObject, ctx: CloneContext): void {
    let obj: Invoice = dbObj as Invoice;

    obj.id = this.id;

    obj.setTitle(this._title);

    obj.setInvoiceDate(this._invoiceDate);

    obj.setDueDate(this._dueDate);

    obj.setAmount(this._amount);

    obj.setRequestCount(this._requestCount);

    obj.setStatus(this._status);

    obj.setInvoiceId(this._invoiceId);

    obj.setInvoiceMonth(this._invoiceMonth);

    obj.setHospital(this._hospital);
  }
  public set(field: number, value: any): void {
    switch (field) {
      case Invoice._TITLE: {
        this.setTitle(value as string);
        break;
      }

      case Invoice._INVOICEDATE: {
        this.setInvoiceDate(value as DateTime);
        break;
      }

      case Invoice._DUEDATE: {
        this.setDueDate(value as DateTime);
        break;
      }

      case Invoice._AMOUNT: {
        this.setAmount(value as number);
        break;
      }

      case Invoice._REQUESTCOUNT: {
        this.setRequestCount(value as number);
        break;
      }

      case Invoice._STATUS: {
        this.setStatus(PaymentStatus.values[value as number]);
        break;
      }

      case Invoice._INVOICEID: {
        this.setInvoiceId(value as string);
        break;
      }

      case Invoice._INVOICEMONTH: {
        this.setInvoiceMonth(value as number);
        break;
      }

      case Invoice._HOSPITAL: {
        this.setHospital(value as Hospital);
        break;
      }
    }
  }
}
